import React from 'react';
import history from 'helpers/history';

import { Plus, Edit3, Trash2, Package, DollarSign, FileText, Eye, Info } from 'lucide-react';
import axios from 'helpers/axios';
import config from 'helpers/config';
import CannedJob from 'models/CannedJobModel';
import CannedJobModal from './CannedJobModal';
import Price from 'components/common/Price';

interface Props {}

interface State {
    cannedjobs: CannedJob[];
    isLoading: boolean;
    isCreateCannedJobModalOpen: boolean;
    isEditCannedJobModalOpen: boolean;
    editableCannedJobId: string | null;
}

class CannedJobSettingsPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            cannedjobs: [],
            isLoading: true,
            isCreateCannedJobModalOpen: false,
            isEditCannedJobModalOpen: false,
            editableCannedJobId: null,
        };
    }

    componentDidMount() {
        this.getCannedJobs();
    }

    getCannedJobs() {
        axios.get(`${config.apiUrl}/cannedjob`).then((res) => this.setState({ cannedjobs: res.data }));
    }
    editCannedJob(e: React.MouseEvent, cannedjobId: string) {
        e.stopPropagation();
        this.setState({ isEditCannedJobModalOpen: true, editableCannedJobId: cannedjobId });
    }

    goToCannedJobDetails(cannedjobId: string) {
        history.push(`cannedjob/${cannedjobId}`);
    }

    handleClose(created: boolean | undefined, edited: boolean | undefined, id?: string) {
        if (created || edited) {
            this.getCannedJobs();
        }
        this.setState({
            isCreateCannedJobModalOpen: false,
            isEditCannedJobModalOpen: false,
            editableCannedJobId: null,
        });
    }
    render() {
        const { cannedjobs, isCreateCannedJobModalOpen, isEditCannedJobModalOpen, editableCannedJobId } = this.state;

        // Calculate statistics
        const totalJobs = cannedjobs.length;
        const totalValue = cannedjobs.reduce((sum, job) => sum + (job.total || 0), 0);
        const avgJobValue = totalJobs > 0 ? totalValue / totalJobs : 0;

        return (
            <div className="flex flex-col min-h-screen bg-gray-50">
                {/* Header */}
                <div className="bg-white border-b border-gray-200 px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-2">
                                <span>Settings</span>
                                <span>/</span>
                                <span className="text-blue-600 font-semibold">Canned Jobs</span>
                            </nav>
                            <h2 className="text-3xl font-bold text-gray-900">Canned Jobs</h2>
                            <p className="text-gray-600 mt-1">
                                Create and manage pre-configured service templates with standardized pricing and line
                                items to streamline your repair order process.
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() =>
                                    this.setState({ isCreateCannedJobModalOpen: !isCreateCannedJobModalOpen })
                                }
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
                            >
                                <Plus className="h-4 w-4" />
                                <span>Add Canned Job</span>
                            </button>
                        </div>
                    </div>
                </div>
                <div className="flex-1 p-8">
                    {/* Statistics Dashboard */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <Package className="h-6 w-6 text-blue-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Total Jobs</p>
                                    <p className="text-2xl font-bold text-gray-900">{totalJobs}</p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <DollarSign className="h-6 w-6 text-green-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Total Value</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        <Price price={totalValue} />
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="flex items-center">
                                <div className="p-2 bg-purple-100 rounded-lg">
                                    <FileText className="h-6 w-6 text-purple-600" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-sm font-medium text-gray-600">Average Value</p>
                                    <p className="text-2xl font-bold text-gray-900">
                                        <Price price={avgJobValue} />
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Canned Jobs List */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">Canned Jobs List</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                Click on any job to view details, or use the action buttons to edit
                            </p>
                        </div>

                        {cannedjobs && cannedjobs.length > 0 ? (
                            <div className="divide-y divide-gray-200">
                                {cannedjobs.map((cannedjob, index) => (
                                    <div
                                        key={cannedjob.id}
                                        className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={() => this.goToCannedJobDetails(cannedjob.id)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start space-x-4">
                                                    <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                                                        <Package className="h-5 w-5 text-blue-600" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="text-lg font-semibold text-gray-900 truncate">
                                                            {cannedjob.title}
                                                        </h4>
                                                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                                            {cannedjob.description || 'No description provided'}
                                                        </p>
                                                        <div className="flex items-center space-x-6 mt-3">
                                                            <div className="flex items-center text-sm text-gray-500">
                                                                <FileText className="h-4 w-4 mr-1" />
                                                                <span>{cannedjob.items?.length || 0} items</span>
                                                            </div>
                                                            <div className="flex items-center text-sm text-gray-500">
                                                                <span className="font-medium ">
                                                                    Job Total:
                                                                    <span className="font-semibold ml-1">
                                                                        <Price price={cannedjob.total} />
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2 ml-4">
                                                <button
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        this.goToCannedJobDetails(cannedjob.id);
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="View details"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={(e: React.MouseEvent) =>
                                                        this.editCannedJob(e, String(cannedjob.id))
                                                    }
                                                    className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                                    title="Edit job"
                                                >
                                                    <Edit3 className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={(e: React.MouseEvent) => {
                                                        e.stopPropagation();
                                                        // TODO: Implement delete functionality
                                                    }}
                                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete job"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-12 text-center">
                                <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No canned jobs yet</h3>
                                <p className="text-gray-500 mb-6">
                                    Create your first canned job template to streamline your service offerings.
                                </p>
                                <button
                                    onClick={() => this.setState({ isCreateCannedJobModalOpen: true })}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 mx-auto transition-colors"
                                >
                                    <Plus className="h-4 w-4" />
                                    <span>Create First Job</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Info Panel */}
                    <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex gap-4">
                            <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800">
                                <h4 className="font-semibold mb-3 text-lg">How Canned Jobs Work</h4>
                                <ul className="space-y-2 text-blue-700">
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Canned jobs are pre-configured service templates with standardized pricing and
                                        items
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Use them to quickly create repair orders for common services you offer
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Each job can include multiple labor items, parts, and fees with preset prices
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                                        Click on any job to view full details, edit pricing, or modify included services
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Modals */}
                {isCreateCannedJobModalOpen && (
                    <CannedJobModal onClose={(created, edited, id) => this.handleClose(created, edited, id)} />
                )}{' '}
                {isEditCannedJobModalOpen && editableCannedJobId && (
                    <CannedJobModal
                        // cannedJobId={editableCannedJobId}
                        onClose={(created, edited, id) => this.handleClose(created, edited, id)}
                    />
                )}
            </div>
        );
    }
}

export default CannedJobSettingsPage;
